import {isFeatureEnabled} from 'feature-flags';
import {FormControl, FormField, plainTextLength} from 'platform/components';
import {VStack, Text, HStack, Icon} from 'platform/foundation';

import {useController} from 'react-hook-form';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useAdvertisementPlatformFilter} from '../hooks/useAdvertisementPlatformFilter';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';

interface DescriptionFormFieldProps extends TestIdProps {
  vehicleId: string;
  control: FormControl<GeneralSettingsFormValues>;
  descriptionLabel: string;
  isDisabled?: boolean;
}

export function DescriptionFormField(props: DescriptionFormFieldProps) {
  const {field} = useController({name: 'description', control: props.control});

  const {platforms} = useAdvertisementPlatformFilter({
    vehicleId: props.vehicleId,
  });

  const isRichTextEnabled = isFeatureEnabled(featureFlags.ADVERTISEMENT_RICH_TEXT_DESCRIPTION);
  const platformHelperTexts = platforms.filter((platform) => platform.enabled);
  const currentLength = isRichTextEnabled
    ? plainTextLength(field.value)
    : (field.value?.length ?? 0);

  return (
    <VStack spacing={1}>
      <FormField
        control={props.control}
        label={props.descriptionLabel}
        placeholder={i18n.t('page.advertisement.labels.descriptionPlaceholder')}
        name="description"
        type={isRichTextEnabled ? 'richText' : 'textarea'}
        isScrollable
        isResizable
        isDisabled={props.isDisabled}
        rows={15}
        data-testid={suffixTestId('description', props)}
      />
      <HStack spacing={1} align="center">
        {platformHelperTexts.map((platform) => {
          const limit = knownPlatformAdvertisementTextLimitation[platform.platform.code];
          if (!limit) {
            return null; // Zero and nil values are hidden
          }

          const isOverLimit = currentLength >= limit;
          return (
            <HStack key={platform.platform.code} spacing={1} align="center">
              <Icon
                size={5}
                value={isOverLimit ? 'alert/warning' : 'action/check_circle'}
                color={isOverLimit ? 'severity.warning' : 'severity.success'}
              />
              <Text size="xSmall" color="secondary">
                {`${currentLength} / ${limit} (${platform.platform.title})`}
              </Text>
            </HStack>
          );
        })}
      </HStack>
    </VStack>
  );
}

const knownPlatformAdvertisementTextLimitation: Partial<Record<string, number>> = {
  sauto: 1000,
  'mobile.de': 5000,
  autocaris: 4000,
  otomoto: 4000,
};
